/**
 * React Query Mutation Hook for recording invoice payment
 * POST /api/v1/invoices/{id}/payments
 */

import { useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';
import { RecordPaymentRequest, RecordPaymentResponse, ApiErrorResponse } from '@/types';
import { apiClient } from '@/api/client';
import { INVOICE_DETAILS_QUERY_KEY } from './useInvoiceDetails';

interface RecordPaymentVariables {
  invoiceId: number;
  payment: RecordPaymentRequest;
}

/**
 * Record a payment for an invoice
 * Automatically invalidates invoice details query on success
 */
export const useRecordPayment = (
  options?: Omit<
    UseMutationOptions<RecordPaymentResponse, ApiErrorResponse, RecordPaymentVariables>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<RecordPaymentResponse, ApiErrorResponse, RecordPaymentVariables>({
    mutationFn: async ({ invoiceId, payment }: RecordPaymentVariables) => {
      const response = await apiClient.post<RecordPaymentResponse>(
        `/commerce-service/api/v1/invoices/${invoiceId}/payments`,
        payment
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate invoice details to refetch with updated payment
      queryClient.invalidateQueries({
        queryKey: [INVOICE_DETAILS_QUERY_KEY, variables.invoiceId]
      });

      // Also invalidate invoice list if it exists
      queryClient.invalidateQueries({
        queryKey: ['invoiceList']
      });
    },
    ...options,
  });
};
